/**
 * Provider search results — text and rating normalisation (finding #2b).
 *
 * Searchanise renders its own cards inside #snize_results, so the pieces of the
 * theme's product card that are content rather than style have to be applied
 * after the app paints:
 *
 *   - credit  "By © Dr. Seuss / The Cat in the Hat™"  ->  "© Dr. Seuss / …"
 *             (snippets/product-card.liquid does the same with
 *              `vendor | remove: 'By'` and a © prepended when one is missing)
 *   - reviews "2 review(s)"  ->  "(2)"  next to the stars, matching the bracketed
 *             Okendo count on the theme card, with the word kept for screen
 *             readers
 *   - stars   the app writes the fill percentage into a `stars-NN` class; this
 *             copies it to --dac-star-fill so the stylesheet can paint the
 *             filled part in the theme's yellow instead of black
 *
 * Everything else about the card is CSS, in assets/component-search-results.css.
 *
 * Runs on the app's own document events when jQuery is present and on a
 * MutationObserver otherwise; both paths are idempotent, so re-running on every
 * filter, sort or paginate is free.
 */
(function () {
  'use strict';

  var RESULTS_ID = 'snize_results';
  var applying = false;

  function creditText(value) {
    var text = String(value).replace(/\s+/g, ' ').trim();
    if (!text) return text;
    // "By X" is this catalogue's artist credit; the theme card strips the "By"
    // and shows a © instead. Anything that already carries a © keeps it.
    text = text.replace(/^by\s+/i, '');
    if (text.indexOf('©') === -1) text = '© ' + text;
    return text;
  }

  function applyCredit(element) {
    if (element.getAttribute('data-dac-credit') === '1') return;

    // The app puts an optional label span first, then a bare text node. Keep the
    // span; rewrite only the text.
    var textNodes = [];
    for (var i = 0; i < element.childNodes.length; i++) {
      if (element.childNodes[i].nodeType === 3) textNodes.push(element.childNodes[i]);
    }

    var current = textNodes.length
      ? textNodes.map(function (node) { return node.nodeValue; }).join('')
      : element.textContent;
    var next = creditText(current);
    if (!next) return;

    if (next !== current.replace(/\s+/g, ' ').trim()) {
      if (textNodes.length) {
        textNodes[0].nodeValue = next;
        for (var j = 1; j < textNodes.length; j++) textNodes[j].nodeValue = '';
      } else {
        element.textContent = next;
      }
    }
    element.setAttribute('data-dac-credit', '1');
  }

  function applyReviewCount(element) {
    if (element.getAttribute('data-dac-reviews') === '1') return;

    var match = element.textContent.match(/(\d[\d,]*)\s*review/i);
    if (!match) return;

    var count = match[1];
    var plain = Number(count.replace(/,/g, ''));

    element.textContent = '(' + count + ')';

    var label = document.createElement('span');
    label.className = 'snize-visually-hidden';
    label.textContent = ' ' + (plain === 1 ? "recensione" : "recensioni");
    element.appendChild(label);

    element.setAttribute('data-dac-reviews', '1');
  }

  function applyStarFill(element) {
    var match = element.className.match(/(?:^|\s)stars-(\d{1,3})(?:\s|$)/);
    if (!match) return;
    var percent = Math.max(0, Math.min(100, Number(match[1]))) + '%';
    if (element.style.getPropertyValue('--dac-star-fill') === percent) return;
    element.style.setProperty('--dac-star-fill', percent);
  }

  function apply(root) {
    if (!root || applying) return;
    applying = true;
    try {
      root.querySelectorAll('.snize-attribute, .snize-vendor').forEach(applyCredit);
      root.querySelectorAll('.snize-reviews .total-reviews').forEach(applyReviewCount);
      root.querySelectorAll('.snize-reviews .stars-container').forEach(applyStarFill);
    } catch (error) {
      /* Leave the provider's own output alone if anything here fails. */
    }
    applying = false;
  }

  function applyEverywhere() {
    apply(document.getElementById(RESULTS_ID));
    apply(document.querySelector('.snize-ac-results'));
  }

  function start() {
    applyEverywhere();

    // The app's own events fire after each render; they carry the container.
    var jq = window.jQuery || window.$;
    if (jq && jq.fn && typeof jq.fn.on === 'function') {
      jq(document).on(
        'Searchanise.ResultsUpdated Searchanise.AutocompleteUpdated',
        function () { applyEverywhere(); }
      );
    }

    // Filtering, sorting and paging replace the list without an event in some
    // paths, and the autocomplete panel is created on demand, so watch as well.
    var observer = new MutationObserver(function () {
      if (applying) return;
      applyEverywhere();
    });

    var results = document.getElementById(RESULTS_ID);
    if (results) observer.observe(results, { childList: true, subtree: true });
    observer.observe(document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
